import React, { useState, useCallback, useMemo } from "react";
import {
    CrossIcon,
    StyledLabel,
    StyledTextInput,
} from "@aureskonnect/react-ui";
import { AvForm, AvGroup } from "availity-reactstrap-validation";
import classnames from "classnames";
import { useTranslation } from "react-i18next";
import { useSnapshot } from "valtio";
import { uuid } from "uuidv4";

import { resetAllPeripherals } from "@pages/Applications/DeviceAssociateModal/store";
import {
    CustomCardHeader,
    CustomCardBody,
    CustomCardFooter,
} from "@components/Common/CustomCardStyled";
import storeVatRate, {
    setConfirmationModificationClick,
    setConfirmationArchivedExistMessageClick,
    setIsFormShopUpdatedPays,
    setIsActionsClicked,
    setActions,
    setIsEdited,
    setKey,
} from "../store";
import { arrayDiff, searchPosInArrayObject } from "@helpers/general";
import MultiCustomCreatableSelection from "@components/Common/MultiCustomCreatableSelection";
import MultiCustomSelect from "@components/Common/MultiCustomSelect";
import ErrorToast from "@components/Common/ErrorTost";
import { CustomMainColorButton } from "@components/Common/CustomMainColorButton";
import { store } from "../../../../components/VerticalLayout/store";
import { CustomSecondaryColorButton } from "@components/Common/CustomSecondaryColorButton";
import { useFetchGlobal } from "../../../../api/hooks";
import { normalizeNumericString } from "@components/Common/DefaultSettingModal/DefaultModalContentWrapper/Tva/helper";
import {
    associate,
    fetchShopsPays,
    makeId,
    paysDiff,
    refreshTags,
    searchPosInArrayObjectVatRateUppercase,
    useFormState,
    useValidation,
    vatRateFormType,
} from "./useVatRateCount";
import VatRateModalsGroup from "./VatRateModalsGroup";
import {
    useRefreshTags,
    useResetErrorsOnEdit,
    useCleanFormOnNewClick,
    useInitVatRateFormData,
} from "./hooks";
import { showSuccessToast } from "@components/Common/SuccessToast";
import FieldError from "./FieldError";

export default function VatRateForm({
    dataVatRate,
    setEditedData,
    isAddVatRateClicked,
    setIsAddVatRateClicked,
    dataTable,
    pays,
    dataState,
    isAddNewVatRateButtonClicked,
    setIsConsult,
    isConsult,
}: vatRateFormType) {
    const { fetchGlobal } = useFetchGlobal();
    const { t } = useTranslation();
    const {
        oneShop,
        shopPays,
        operatorID,
        userID,
        franchiseID,
        shopID,
    } = useSnapshot(store);
    const {
        dataFranchise,
        dataShops,
        confirmationModificationClick,
        confirmationArchivedExistMessageClick,
        isActionsClicked,
        isEdited,
        dataModified,
    } = useSnapshot(storeVatRate);
    const uuidUser = localStorage.getItem("uuidUser");
    const { form, updateField, resetForm, setForm } = useFormState({
        code: "",
        value: "",
        tags: [],
        paysData: [],
    });
    const { errors, setError, resetErrors, validate } = useValidation();
    const [canValidate, setCanValidate] = useState(true);
    const [isSendingTag, setIsSendingTag] = useState(false);
    const [tagsVateRate, setTagsVateRate] = useState<string[]>([]);
    const [type, setType] = useState<"add" | "update" | "updateShop">("add");
    const [canReplace, setCanReplace] = useState(false);
    const [modals, setModals] = useState({
        association: false,
        editMessage: false,
    });
    const [objAssoc, setObjAssoc] = useState<any>({});
    const [franchiseData, setFranchiseData] = useState<any>([]);
    const isEditMode = typeof dataVatRate?.selectedRow === "object";
    const isFieldDisabled = oneShop && isEditMode;
    const dataFran = useMemo(
        () =>
            oneShop
                ? dataShops.find((x: any) => x.shopId === shopID)?.subRows ?? []
                : dataFranchise,
        [oneShop, dataShops, shopID, dataFranchise]
    );
    const paysItems = useMemo(
        () =>
            form.paysData?.length > 0
                ? pays.filter((x: any) => form.paysData.includes(x.label))
                : [],
        [form.paysData, pays]
    );
    const listPaysDiff = useMemo(
        () =>
            isEditMode
                ? fetchShopsPays(dataVatRate.selectedRow.Id, dataShops)
                : [],
        [isEditMode, dataVatRate?.selectedRow?.Id, dataShops]
    );
    const title = useMemo(() => {
        if (isConsult) return t("View VAT rate");
        return dataVatRate.length === 0
            ? t("Add VAT rate")
            : t("Modify VAT rate");
    }, [isConsult, dataVatRate.length, t]);
    const checkDuplicate = useCallback(
        (code: string, value: string): boolean => {
            if (dataFran.length === 0) return false;

            const pos = searchPosInArrayObjectVatRateUppercase(dataFran, {
                code: code.trim(),
                value: value.trim(),
            });

            if (pos === -1) return false;
            if (!isEditMode) return true;

            return (
                code.trim() !==
                    dataVatRate?.selectedRow?.[t("VAT code")]?.trim() ||
                value.trim() !==
                    dataVatRate?.selectedRow?.[t("VAT rate")]?.trim()
            );
        },
        [dataFran, isEditMode, dataVatRate?.selectedRow, t]
    );

    const cleanForm = useCallback(() => {
        setCanValidate(true);
        resetForm();
        resetErrors();
        setIsEdited(false);
    }, [resetForm, resetErrors]);

    useRefreshTags(
        dataVatRate,
        userID,
        franchiseID,
        setTagsVateRate,
        form.tags,
        fetchGlobal
    );
    useResetErrorsOnEdit(
        isAddVatRateClicked,
        dataVatRate,
        isEditMode,
        resetErrors
    );
    useCleanFormOnNewClick(isAddNewVatRateButtonClicked, cleanForm);
    useInitVatRateFormData(
        dataVatRate,
        oneShop,
        t,
        modals.association,
        confirmationArchivedExistMessageClick,
        setForm,
        setType,
        setCanValidate,
        cleanForm
    );

    const handleClose = useCallback(() => {
        setIsConsult(false);
        const closeAction = () => {
            setIsEdited(false);
            resetAllPeripherals();
            setEditedData([]);
            setIsAddVatRateClicked(!isAddVatRateClicked);
            cleanForm();
        };

        if (isEdited) {
            setIsActionsClicked(!isActionsClicked);
            setActions(closeAction);
        } else {
            closeAction();
        }
    }, [
        isEdited,
        isActionsClicked,
        isAddVatRateClicked,
        setEditedData,
        setIsAddVatRateClicked,
        setIsConsult,
        cleanForm,
    ]);

    const handleCodeChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const value = e.target.value;
            const numValue = value !== "" ? Number(value).toString() : "";
            const isEmpty = value.trim() === "";
            const isDuplicate = checkDuplicate(numValue, form.value);
            setIsEdited(true);
            updateField("code", numValue);
            setError("code", isEmpty);
            setError("codeExists", isDuplicate);
            setCanValidate(false);
        },
        [checkDuplicate, form.value, updateField, setError]
    );

    const handleValueChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const rawValue = e.target.value;
            const numValue = rawValue !== "" ? Number(rawValue).toString() : "";
            const isEmpty = rawValue.trim() === "";
            const isInvalid =
                !isEmpty && (Number(rawValue) < 0 || Number(rawValue) > 100);
            const isDuplicate = checkDuplicate(form.code, numValue);
            setIsEdited(true);
            updateField("value", numValue);
            setError("value", isEmpty);
            setError("valueInvalid", isInvalid);
            setError("codeExists", isDuplicate);
            setCanValidate(false);
        },
        [checkDuplicate, form.code, updateField, setError]
    );

    const handlePaysChange = useCallback(() => {
        setIsEdited(true);
        setCanValidate(false);
        setError("pays", false);
    }, [setError]);

    const buildRequestData = useCallback(
        (id: string, shopId: any, canReplaceFlag: boolean) => ({
            userId: userID,
            operatorId: operatorID,
            uuidUser,
            shopId,
            franchiseId: franchiseID,
            canReplace: canReplaceFlag,
            data: [
                {
                    id,
                    code: normalizeNumericString(form.code),
                    value: form.value,
                    tag: form.tags,
                    status: isEditMode
                        ? Object.keys(dataState).length === 0
                            ? dataVatRate.selectedRow.status
                            : dataState.state
                        : true,
                    pays: paysItems.map((a: any) => a.value),
                    archived: isEditMode
                        ? dataVatRate.selectedRow.archived
                        : false,
                    isFavorite: isEditMode
                        ? dataVatRate.selectedRow.isFavorite
                        : dataFranchise.length === 0,
                },
            ],
        }),
        [
            form,
            paysItems,
            isEditMode,
            dataVatRate,
            dataState,
            userID,
            operatorID,
            uuidUser,
            franchiseID,
            dataFranchise.length,
        ]
    );

    const handleValidate = useCallback(
        async (
            object: any,
            submitType: string,
            notify?: any,
            isFavoriteShop?: boolean
        ) => {
            if (canValidate) return;
            setCanValidate(true);

            const apiUrl =
                submitType === "add"
                    ? `${process.env.REACT_APP_API_V2_URL}/settings/general/vatRate`
                    : `${process.env.REACT_APP_API_V2_URL}/settings/general/editVatRate`;

            try {
                let payload: any;

                if (submitType === "add") {
                    // Ajout: garder le format original
                    payload = object;
                } else {
                    // Update: construire les données pour franchise + boutiques
                    const vatRateData = {
                        id: object.data[0].id,
                        code: Number(object.data[0].code).toString(),
                        value: object.data[0].value,
                        tag: object.data[0].tag,
                        status: object.data[0].status,
                        pays: object.data[0].pays,
                        archived: object.data[0].archived,
                        isFavorite: object.data[0].isFavorite,
                    };

                    const allVatRates: any[] = [];

                    // Ajouter la franchise (shopId = 0)
                    allVatRates.push({
                        ...vatRateData,
                        shopId: 0,
                    });

                    // Ajouter toutes les boutiques associées
                    if (dataShops && dataShops.length > 0) {
                        for (const shop of dataShops) {
                            // Vérifier si cette TVA existe dans la boutique
                            const tvaInShop = shop.subRows?.find(
                                (tva: any) => tva.Id === vatRateData.id
                            );
                            if (tvaInShop) {
                                allVatRates.push({
                                    ...vatRateData,
                                    shopId: shop.shopId,
                                    isFavorite: tvaInShop.isFavorite,
                                });
                            }
                        }
                    }

                    payload = {
                        userId: userID,
                        franchiseId: franchiseID,
                        operatorId: operatorID,
                        uuidUser: uuidUser,
                        canReplace: object.canReplace ?? false,
                        data: allVatRates,
                    };
                }
                const requestOptions = {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(payload),
                };

                const data: any = await fetchGlobal(apiUrl, requestOptions);

                if (data.error) throw new Error("error");

                setIsSendingTag(true);

                if (oneShop && submitType === "updateShop") {
                    setKey(uuid());
                    setIsEdited(false);
                    resetAllPeripherals();
                    setIsAddVatRateClicked(false);
                    refreshTags(
                        userID,
                        franchiseID,
                        setTagsVateRate,
                        form.tags,
                        fetchGlobal
                    );
                    return;
                }

                if (submitType === "add" && oneShop) {
                    const result = await handleShopAddition(
                        data,
                        object,
                        isFavoriteShop
                    );
                    if (!result.shouldContinue) {
                        notify = false;
                    }
                }

                if (submitType === "update" || submitType === "updateShop") {
                    if (notify !== false) {
                        showSuccessToast(
                            t("The vat rate is changed successfully")
                        );
                    }
                } else if (submitType === "add" && notify !== false) {
                    showSuccessToast(t("The vat rate is successfully added"));
                }

                setKey(uuid());
                setIsEdited(false);
                resetAllPeripherals();
                cleanForm();
            } catch (e) {
                ErrorToast(t("There's an error"));
            } finally {
                setCanValidate(false);
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [
            canValidate,
            oneShop,
            userID,
            franchiseID,
            operatorID,
            uuidUser,
            dataShops,
            form.tags,
            t,
            cleanForm,
            setIsAddVatRateClicked,
        ]
    );

    const handleShopAddition = async (
        data: any,
        object: any,
        isFavoriteShop?: boolean
    ) => {
        const baseAssoc = {
            userId: userID,
            operatorId: operatorID,
            uuidUser,
            franchiseId: franchiseID,
            canReplace: true,
            shopId: shopID,
        };

        if (!data?.exist?.id) {
            const singleAssociation = {
                shopId: shopID,
                data: {
                    ...object.data[0],
                    status: dataModified.length === 0,
                    isFavorite: isFavoriteShop,
                },
            };
            const assocObj = {
                userId: userID,
                operatorId: operatorID,
                uuidUser,
                franchiseId: franchiseID,
                canReplace: true,
                associations: [singleAssociation], // ← IDENTIQUE AU BULK !
            };
            await associate(assocObj, setIsSendingTag, fetchGlobal);
            return { shouldContinue: true };
        }
        setFranchiseData(data.exist);
        const paysLabels =
            data.exist.pays.length > 0
                ? pays
                      .filter((x: any) => data.exist.pays.includes(x.value))
                      .map((y: any) => y.label)
                : [];
        const assocData = {
            ...data.exist,
            isFavorite: isFavoriteShop,
            tag: form.tags,
        };
        if (paysLabels.includes(shopPays) && !data.exist.archived) {
            setObjAssoc({
                ...baseAssoc,
                data: [{ ...assocData, status: false }],
            });
            setModals((prev) => ({ ...prev, association: true }));
            return { shouldContinue: false };
        }

        if (data.exist.archived) {
            setIsFormShopUpdatedPays(false);
            setConfirmationArchivedExistMessageClick(true);
            return { shouldContinue: false };
        }
        const payIndex = searchPosInArrayObject(pays, "label", shopPays);
        data.exist.pays = [...data.exist.pays, pays[payIndex].value];
        setObjAssoc({
            ...baseAssoc,
            data: [{ ...data.exist, tag: form.tags }],
            baseCountry: data.exist.pays,
        });
        setIsFormShopUpdatedPays(true);
        setModals((prev) => ({ ...prev, association: true }));
        return { shouldContinue: false };
    };

    const handleValidateClick = useCallback(() => {
        const isValid = validate(form, errors.codeExists);
        if (!isValid) return;

        const dataPaysShops = isEditMode
            ? fetchShopsPays(dataVatRate.selectedRow.Id, dataShops)
            : [];

        if (isEditMode) {
            const current =
                dataTable.data[
                    searchPosInArrayObject(
                        dataTable.data,
                        "Id",
                        dataVatRate.selectedRow.Id
                    )
                ];
            const tagDiff = arrayDiff(current.tag, form.tags);
            const countryDiff = arrayDiff(
                current[t("Country")].labels,
                form.paysData
            );

            if (
                !tagDiff.length &&
                !countryDiff.length &&
                current.code === form.code &&
                current.value === form.value
            ) {
                setModals((prev) => ({ ...prev, editMessage: true }));
                return;
            }

            if (paysDiff(dataPaysShops, form.paysData).length > 0) {
                ErrorToast(
                    t(
                        "Attention ! You cannot remove the country of a VAT assigned to the store(s)"
                    )
                );
                return;
            }
        }

        if (isEditMode && oneShop) {
            setType("updateShop");
            handleValidate(
                buildRequestData(dataVatRate.selectedRow.Id, shopID, false),
                "updateShop"
            );
        } else if (isEditMode) {
            setType("update");
            const shopCount = dataShops.filter(
                (el: any) =>
                    searchPosInArrayObject(
                        el.subRows,
                        "Id",
                        dataVatRate.selectedRow.Id
                    ) !== -1
            ).length;

            if (shopCount > 0) {
                setConfirmationModificationClick(
                    !confirmationModificationClick
                );
            } else {
                setCanReplace(true);
                handleValidate(
                    buildRequestData(dataVatRate.selectedRow.Id, 0, true),
                    "update"
                );
            }
        } else {
            setType("add");
            handleValidate(
                buildRequestData(makeId(10), 0, false),
                "add",
                undefined,
                dataTable.data.length === 0
            );
        }
    }, [
        form,
        errors.codeExists,
        validate,
        isEditMode,
        oneShop,
        dataVatRate,
        dataTable,
        dataShops,
        shopID,
        confirmationModificationClick,
        t,
        buildRequestData,
        handleValidate,
    ]);
    const formatCreateLabel = (inputValue: string) => (
        <span>
            {inputValue} ({t("New tag")})
        </span>
    );
    return (
        <>
            <AvForm className="card">
                <CustomCardHeader
                    className="d-flex align-items-center justify-content-between"
                    style={{ maxHeight: "76px" }}
                >
                    <h5
                        className={classnames(
                            "pt-3 tva_txt_FRtitleFormVatRate",
                            {
                                tva_txt_FRmodifyVatRate:
                                    dataVatRate.length !== 0,
                            }
                        )}
                    >
                        {title}
                    </h5>
                    <CrossIcon
                        style={{ cursor: "pointer" }}
                        className="tva_icn_FRcloseForm"
                        height={13}
                        width={13}
                        onClick={handleClose}
                    />
                </CustomCardHeader>

                <CustomCardBody
                    className="pl-3 pr-5"
                    style={{ overflowY: "auto", maxHeight: "470px" }}
                >
                    <AvGroup className={isConsult ? "pe-none__clz" : ""}>
                        <StyledLabel
                            htmlFor="path"
                            className="required__clz mt-3"
                        >
                            {t("VAT code")}
                        </StyledLabel>
                        <StyledTextInput
                            name="code"
                            className={classnames("tva_inp_FRvatCode", {
                                input__clz:
                                    errors.code ||
                                    errors.codeExists ||
                                    errors.valueInvalid,
                                "not-allowed-input-vr__clz": isFieldDisabled,
                            })}
                            placeholder={t("Write")}
                            type="number"
                            autoComplete="off"
                            value={form.code}
                            onChange={handleCodeChange}
                            autoFocus={!isFieldDisabled && !isConsult}
                        />
                        <FieldError
                            show={errors.codeExists}
                            message={t(
                                "The combination VAT code, VAT rate already exists"
                            )}
                        />
                        <FieldError
                            show={errors.code}
                            message={t("Please enter a vat code")}
                        />
                        <FieldError
                            show={errors.valueInvalid}
                            message={t("Vat code is invalid")}
                        />
                    </AvGroup>
                    <AvGroup className={isConsult ? "pe-none__clz" : ""}>
                        <StyledLabel
                            htmlFor="path"
                            className="required__clz mt-3"
                        >
                            {t("VAT rate")}
                        </StyledLabel>
                        <StyledTextInput
                            name="tva_inp"
                            className={classnames("tva_inp_FRvatRate", {
                                input__clz:
                                    errors.value ||
                                    errors.codeExists ||
                                    errors.valueInvalid,
                                "not-allowed-input-vr__clz": isFieldDisabled,
                            })}
                            placeholder={t("Write")}
                            type="number"
                            // step="0.00"
                            min="0"
                            value={form.value}
                            autoComplete="off"
                            onChange={handleValueChange}
                        />
                        <FieldError
                            show={errors.value}
                            message={t("Please enter a vat rate")}
                        />
                        <FieldError
                            show={errors.valueInvalid}
                            message={t("Vat rate is invalid")}
                        />
                        <FieldError
                            show={errors.codeExists}
                            message={t(
                                "The combination VAT code, VAT rate already exists"
                            )}
                        />
                    </AvGroup>

                    <AvGroup className={isConsult ? "pe-none__clz" : ""}>
                        <StyledLabel
                            htmlFor="path"
                            className="required__clz mt-3"
                        >
                            {t("Country")}
                        </StyledLabel>
                        <MultiCustomSelect
                            onDelete={() => {
                                setIsEdited(true);
                                setCanValidate(false);
                            }}
                            blocked={isFieldDisabled}
                            result={form.paysData || []}
                            setResult={(data: string[]) =>
                                updateField("paysData", data)
                            }
                            data={
                                oneShop
                                    ? [
                                          pays[
                                              searchPosInArrayObject(
                                                  pays,
                                                  "label",
                                                  shopPays
                                              )
                                          ]?.label,
                                      ]
                                    : pays.map((a: any) => a.label)
                            }
                            onChange={handlePaysChange}
                            className={classnames("tva_inp_FRcountry", {
                                invalid__clz: errors.pays,
                                "not-allowed-input-vr__clz": isFieldDisabled,
                            })}
                            placeholder={`${t("Select")}…`}
                            value={t("Choose")}
                            name="country"
                            blockedList={listPaysDiff}
                        />
                        <FieldError
                            show={errors.pays}
                            message={t("Please enter thee country of vat rate")}
                        />
                    </AvGroup>

                    <AvGroup className={isConsult ? "pe-none__clz" : ""}>
                        <StyledLabel htmlFor="path" className="mt-3">
                            {t("Tag")}
                        </StyledLabel>
                        <MultiCustomCreatableSelection
                            isSendingTag={isSendingTag}
                            onDelete={() => {
                                setIsEdited(true);
                                setCanValidate(false);
                            }}
                            onCreateOption={() => setIsEdited(true)}
                            className="tva_inp_FRtags"
                            setResult={(data: string[]) =>
                                updateField("tags", data)
                            }
                            result={form.tags}
                            data={tagsVateRate}
                            onChange={() => {
                                setIsEdited(true);
                                setCanValidate(false);
                            }}
                            placeholder={`${t("Select")}…`}
                            value=""
                            name="tva_inp"
                            noOptionsMessage={() => t("No options")}
                            formatCreateLabel={formatCreateLabel}
                        />
                    </AvGroup>
                </CustomCardBody>

                <CustomCardFooter className="pt-1 pb-2">
                    <CustomSecondaryColorButton
                        outline
                        rounded
                        variant="light"
                        className="mr-2 tva_btn_FRcancel cmn_btn_cancel"
                        onClick={handleClose}
                    >
                        <span>{t("Cancel")}</span>
                    </CustomSecondaryColorButton>
                    <CustomMainColorButton
                        rounded
                        className={classnames(
                            "cmn_btn_validate tva_btn_FRvalidate",
                            { "not-allowed-input-vr__clz": isConsult }
                        )}
                        variant="primary"
                        onClick={handleValidateClick}
                    >
                        {t("Validate")}
                    </CustomMainColorButton>
                </CustomCardFooter>
            </AvForm>

            <VatRateModalsGroup
                confirmationModificationClick={confirmationModificationClick}
                confirmationArchivedExistMessageClick={
                    confirmationArchivedExistMessageClick
                }
                confirmationEditMessageClick={modals.editMessage}
                confirmationAssociationClick={modals.association}
                handleValidate={handleValidate}
                type={type}
                canReplace={canReplace}
                setIsAddVatRateClicked={setIsAddVatRateClicked}
                setIsEdited={setIsEdited}
                setConfirmationArchivedExistMessageClick={
                    setConfirmationArchivedExistMessageClick
                }
                setConfirmationEditMessageClick={(v: boolean) =>
                    setModals((prev) => ({ ...prev, editMessage: v }))
                }
                setConfirmationAssociationClick={(v: boolean) =>
                    setModals((prev) => ({ ...prev, association: v }))
                }
                isAddVatRateClicked={isAddVatRateClicked}
                objAssoc={objAssoc}
                franchiseData={franchiseData}
                setIsSendingTag={setIsSendingTag}
                setCanValidate={setCanValidate}
                userID={userID}
                operatorID={operatorID}
                uuidUser={uuidUser}
                franchiseID={franchiseID}
                dataVatRate={dataVatRate}
                codeVateRate={form.code}
                vateRate={form.value}
                tagVateRateItem={form.tags}
                paysVateRateItem={paysItems}
            />
        </>
    );
}
